import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import enhancedApiClient from '../api/enhancedApiClient';

const MessagesPage = ({ isVendor = false }) => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [conversations, setConversations] = useState([]);
  const [lastMessages, setLastMessages] = useState({});
  const [selectedConv, setSelectedConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [message, setMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [eventId, setEventId] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [participantNames, setParticipantNames] = useState({});
  const [showNewConv, setShowNewConv] = useState(false);
  const [selectedParticipants, setSelectedParticipants] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    const init = async () => {
      try {
        const { Auth } = await import('aws-amplify');
        const user = await Auth.currentAuthenticatedUser();
        setCurrentUser(user);
        const profile = await enhancedApiClient.getUser(user.username);
        setUserProfile(profile);
        const eid = profile?.currentEventId;
        setEventId(eid);

        if (eid) {
          // Load vendors for this event and build name map
          const names = {};
          names[user.username] = 'You';
          const vendorList = [];

          const vRes = await fetch(`https://etz8ecs10a.execute-api.ap-southeast-2.amazonaws.com/prod/api/events/${eid}/vendors`);
          if (vRes.ok) {
            const associations = await vRes.json();

            for (const assoc of associations) {
              if (!assoc.vendorId || assoc.vendorId === 'undefined') continue;
              try {
                // Get vendor business name
                const vr = await fetch(`https://etz8ecs10a.execute-api.ap-southeast-2.amazonaws.com/prod/api/vendors/${assoc.vendorId}`);
                if (vr.ok) {
                  const vd = await vr.json();
                  const businessName = vd.name?.S || vd.name || assoc.vendorId;

                  // Find the user account for this vendor
                  const userId = `${assoc.vendorId}-user`;
                  try {
                    const uRes = await fetch(`https://etz8ecs10a.execute-api.ap-southeast-2.amazonaws.com/prod/api/users/${userId}`);
                    if (uRes.ok) {
                      const uData = await uRes.json();
                      if (uData?.id) {
                        names[uData.id] = businessName;
                        vendorList.push({ id: assoc.vendorId, name: businessName, userId: uData.id });
                      }
                    }
                  } catch {}

                  // Also map the vendorId itself in case it appears as a participant
                  names[assoc.vendorId] = businessName;
                }
              } catch {}
            }
          }
          setVendors(vendorList);

          // Also resolve client name
          try {
            const eventRes = await fetch(`https://etz8ecs10a.execute-api.ap-southeast-2.amazonaws.com/prod/api/events/${eid}`);
            if (eventRes.ok) {
              const eventData = await eventRes.json();
              if (eventData.clientName) {
                // Find client user
                const allConvUsers = Object.keys(names);
                // The current user might be the client
                if (profile?.userType === 'client') {
                  names[user.username] = 'You';
                }
              }
            }
          } catch {}

          // Load conversations
          const convs = await enhancedApiClient.getConversations(eid, user.username);
          setConversations(convs || []);

          // Load last message for each conversation
          const lastMsgs = {};
          for (const conv of convs || []) {
            const cid = conv.conversationId || conv.threadId;
            try {
              const msgs = await enhancedApiClient.getConversationMessages(cid);
              if (msgs && msgs.length > 0) {
                lastMsgs[cid] = msgs[msgs.length - 1];
              }
            } catch {}
          }
          setLastMessages(lastMsgs);

          // Resolve any participant names not yet known
          for (const conv of convs || []) {
            for (const pid of conv.participants || []) {
              if (!names[pid]) {
                try {
                  const p = await enhancedApiClient.getUser(pid);
                  if (p?.currentVendorId) {
                    const vr = await fetch(`https://etz8ecs10a.execute-api.ap-southeast-2.amazonaws.com/prod/api/vendors/${p.currentVendorId}`);
                    if (vr.ok) {
                      const vd = await vr.json();
                      names[pid] = vd.name?.S || vd.name || p.name || pid;
                    } else {
                      names[pid] = p.name || pid;
                    }
                  } else {
                    names[pid] = p?.name || pid;
                  }
                } catch { names[pid] = pid; }
              }
            }
          }
          setParticipantNames(names);

          // Auto-select from URL
          const convId = searchParams.get('conversationId') || location.state?.selectedConversationId;
          if (convId && convs?.length) {
            const found = convs.find(c => (c.conversationId || c.threadId) === convId);
            if (found) setSelectedConv(found.conversationId || found.threadId);
          }
        }
      } catch (e) {
        console.error('Error initializing messages:', e);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  // Load messages when conversation selected
  useEffect(() => {
    if (!selectedConv) return;
    const load = async () => {
      try {
        const msgs = await enhancedApiClient.getConversationMessages(selectedConv);
        setMessages(msgs || []);
        setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);

        // Mark as read
        if (currentUser?.username) {
          await enhancedApiClient.markConversationAsRead(selectedConv, currentUser.username);
        }
      } catch (e) {
        console.error('Error loading messages:', e);
        setMessages([]);
      }
    };
    load();
  }, [selectedConv]);

  const sendMsg = async () => {
    if (!message.trim() || !selectedConv) return;
    try {
      await enhancedApiClient.sendConversationMessage(selectedConv, {
        sender: currentUser?.username,
        content: message
      });
      setMessage('');
      const msgs = await enhancedApiClient.getConversationMessages(selectedConv);
      setMessages(msgs || []);
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch (e) {
      console.error('Error sending message:', e);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedConv) return;
    setUploadingFile(true);
    try {
      const { fileUrl, fileName } = await enhancedApiClient.uploadFile(file);
      await enhancedApiClient.sendConversationMessage(selectedConv, {
        sender: currentUser?.username,
        content: JSON.stringify({ text: message.trim() || '', file: { fileUrl, fileName, fileType: file.type } })
      });
      setMessage('');
      const msgs = await enhancedApiClient.getConversationMessages(selectedConv);
      setMessages(msgs || []);
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch (e) {
      console.error('Error uploading file:', e);
    } finally {
      setUploadingFile(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const createConversation = async () => {
    if (selectedParticipants.length === 0 || !eventId) return;
    try {
      const participants = [currentUser.username, ...selectedParticipants].sort();

      // Check if a conversation with these exact participants already exists
      const existing = conversations.find(c => {
        const cParticipants = [...(c.participants || [])].sort();
        if (cParticipants.length !== participants.length) return false;
        return cParticipants.every((p, i) => p === participants[i]);
      });

      if (existing) {
        // Just open the existing one
        setShowNewConv(false);
        setSelectedParticipants([]);
        setSelectedConv(existing.conversationId || existing.threadId);
        return;
      }

      const result = await enhancedApiClient.createConversation({
        eventId,
        participants,
        createdBy: currentUser.username
      });
      setShowNewConv(false);
      setSelectedParticipants([]);

      const convs = await enhancedApiClient.getConversations(eventId, currentUser.username);
      setConversations(convs || []);
      setSelectedConv(result.conversationId);
    } catch (e) {
      console.error('Error creating conversation:', e);
    }
  };

  const getConvName = (conv) => {
    const others = (conv.participants || []).filter(p => p !== currentUser?.username);
    return others.map(p => participantNames[p] || p).join(', ') || 'Conversation';
  };

  const getConvInitials = (conv) => {
    const others = (conv.participants || []).filter(p => p !== currentUser?.username);
    if (others.length === 1) {
      const name = participantNames[others[0]] || others[0];
      return name.substring(0, 2).toUpperCase();
    }
    // Group chat — first letter of each participant's name
    return others.map(p => (participantNames[p] || p).charAt(0).toUpperCase()).join('').substring(0, 3);
  };

  const getUnreadCount = (conv) => {
    const id = conv.conversationId || conv.threadId;
    const lastRead = conv.lastReadBy?.[currentUser?.username];
    if (!lastRead) return conv.lastMessageAt ? 1 : 0;
    return conv.lastMessageAt && new Date(conv.lastMessageAt) > new Date(lastRead) ? 1 : 0;
  };

  const filteredConvs = conversations.filter(c => {
    if (!searchQuery) return true;
    const name = getConvName(c).toLowerCase();
    const q = searchQuery.toLowerCase();
    if (name.includes(q)) return true;
    // Also search in last message preview
    const cid = c.conversationId || c.threadId;
    const lastMsg = lastMessages[cid];
    if (lastMsg) {
      try {
        const parsed = JSON.parse(lastMsg.content);
        if (parsed.text?.toLowerCase().includes(q)) return true;
      } catch {
        if (lastMsg.content?.toLowerCase().includes(q)) return true;
      }
    }
    return false;
  });

  if (loading) {
    return (
      <div>
        <div style={{ backgroundColor: '#E8F4E8', padding: '48px 24px', textAlign: 'center' }}>
          <h1 style={{ fontSize: '56px', fontWeight: '400', color: '#1F2937', margin: 0, fontFamily: 'Georgia, serif', letterSpacing: '-0.02em' }}>Messages</h1>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: '16px' }}>
          <div style={{ width: '40px', height: '40px', border: '3px solid #E5E7EB', borderTop: '3px solid #6B9080', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
          <div style={{ color: '#6B7280', fontSize: '16px' }}>Loading messages...</div>
          <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ backgroundColor: '#E8F4E8', padding: '48px 24px', textAlign: 'center' }}>
        <h1 style={{ fontSize: '56px', fontWeight: '400', color: '#1F2937', margin: 0, fontFamily: 'Georgia, serif', letterSpacing: '-0.02em' }}>Messages</h1>
      </div>

      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '24px', display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '24px', height: 'calc(100vh - 250px)' }}>
        {/* Left — Conversation list */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', height: '100%', overflow: 'hidden' }}>
          <div style={{ display: 'flex', gap: '8px' }}>
            <input
              type="text" placeholder="Search conversations..." value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ flex: 1, padding: '10px 14px', border: '1px solid #E5E7EB', borderRadius: '8px', fontSize: '14px', backgroundColor: '#F9FAFB' }}
            />
            <button onClick={() => setShowNewConv(true)}
              style={{ padding: '10px 16px', backgroundColor: '#6B9080', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '500', cursor: 'pointer', whiteSpace: 'nowrap' }}>
              + New
            </button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', overflowY: 'auto', flex: 1 }}>
            {filteredConvs.length === 0 ? (
              <div style={{ padding: '40px 20px', textAlign: 'center', color: '#9CA3AF' }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#D1D5DB" strokeWidth="1.5" style={{ margin: '0 auto 12px' }}>
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                </svg>
                <div style={{ fontSize: '14px', marginBottom: '8px' }}>No conversations yet</div>
                <div style={{ fontSize: '13px' }}>Start a new message with your vendors</div>
              </div>
            ) : (
              filteredConvs.map(conv => {
                const id = conv.conversationId || conv.threadId;
                const hasUnread = getUnreadCount(conv) > 0;
                return (
                  <div key={id}
                    onClick={() => setSelectedConv(id)}
                    style={{
                      padding: '14px 16px', backgroundColor: selectedConv === id ? '#E8F4E8' : 'white',
                      borderRadius: '10px', border: '1px solid #E5E7EB', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', gap: '12px', transition: 'all 0.15s'
                    }}
                  >
                    <div style={{
                      width: '40px', height: '40px', backgroundColor: selectedConv === id ? '#6B9080' : '#E8F4E8',
                      borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '14px', fontWeight: '600', color: selectedConv === id ? 'white' : '#6B9080', flexShrink: 0
                    }}>
                      {getConvInitials(conv)}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '15px', fontWeight: hasUnread ? '600' : '500', color: '#1F2937', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                          {getConvName(conv)}
                        </span>
                        {hasUnread && <div style={{ width: '8px', height: '8px', backgroundColor: '#EF4444', borderRadius: '50%', flexShrink: 0 }} />}
                        {lastMessages[id]?.timestamp && (
                          <span style={{ fontSize: '11px', color: '#9CA3AF', flexShrink: 0 }}>
                            {new Date(lastMessages[id].timestamp).toLocaleDateString('en-NZ', { day: 'numeric', month: 'short' })}, {new Date(lastMessages[id].timestamp).toLocaleTimeString('en-NZ', { hour: '2-digit', minute: '2-digit', hour12: true })}
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: '13px', color: '#9CA3AF', marginTop: '3px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {lastMessages[id] ? (() => {
                          const msg = lastMessages[id];
                          const sender = msg.sender === currentUser?.username ? 'You' : (participantNames[msg.sender] || msg.sender);
                          let preview;
                          try {
                            const parsed = JSON.parse(msg.content);
                            preview = parsed.text || (parsed.file ? parsed.file.fileName : msg.content);
                          } catch { preview = msg.content; }
                          return `${sender}: ${preview.substring(0, 40)}${preview.length > 40 ? '...' : ''}`;
                        })() : 'No messages yet'}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Right — Chat */}
        <div style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #E5E7EB', display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
          {/* Chat header */}
          <div style={{ padding: '16px 20px', borderBottom: '1px solid #E5E7EB', flexShrink: 0 }}>
            {selectedConv ? (
              <div style={{ fontSize: '18px', fontWeight: '500', color: '#1F2937' }}>
                {getConvName(conversations.find(c => (c.conversationId || c.threadId) === selectedConv) || {})}
              </div>
            ) : (
              <div style={{ fontSize: '16px', color: '#9CA3AF' }}>Select a conversation</div>
            )}
          </div>

          {/* Messages */}
          <div style={{ flex: 1, padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px', overflowY: 'auto' }}>
            {!selectedConv ? (
              <div style={{ textAlign: 'center', color: '#9CA3AF', padding: '60px 0' }}>
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#D1D5DB" strokeWidth="1.5" style={{ margin: '0 auto 16px' }}>
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                </svg>
                <div style={{ fontSize: '15px' }}>Select a conversation or start a new one</div>
              </div>
            ) : messages.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#9CA3AF', padding: '60px 0', fontSize: '14px' }}>
                No messages yet. Say hello!
              </div>
            ) : (
              messages.map((msg, i) => {
                let parsed;
                try { parsed = JSON.parse(msg.content); } catch { parsed = { text: msg.content }; }
                const isMe = msg.sender === currentUser?.username;
                const senderName = isMe ? 'You' : (participantNames[msg.sender] || msg.sender);
                const msgText = parsed.text || parsed.file?.fileName || '';
                const isSearchMatch = searchQuery && msgText.toLowerCase().includes(searchQuery.toLowerCase());

                return (
                  <div key={i} id={`msg-${i}`} style={{ display: 'flex', flexDirection: 'column', alignItems: isMe ? 'flex-end' : 'flex-start' }}>
                    <div style={{ fontSize: '12px', color: '#9CA3AF', marginBottom: '4px' }}>{senderName}</div>
                    <div style={{
                      maxWidth: '70%', padding: '12px 16px', borderRadius: '12px', fontSize: '14px',
                      backgroundColor: isSearchMatch ? '#FEF3C7' : (isMe ? '#6B9080' : '#F3F4F6'),
                      color: isSearchMatch ? '#92400E' : (isMe ? 'white' : '#1F2937'),
                      border: isSearchMatch ? '2px solid #F59E0B' : 'none'
                    }}>
                      {parsed.text && <div>{parsed.text}</div>}
                      {parsed.file && (
                        <a href={parsed.file.fileUrl} target="_blank" rel="noopener noreferrer"
                          style={{
                            display: 'flex', alignItems: 'center', gap: '8px', marginTop: parsed.text ? '8px' : '0',
                            padding: '8px', backgroundColor: isMe ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.05)',
                            borderRadius: '6px', color: 'inherit', textDecoration: 'none'
                          }}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14,2 14,8 20,8"/></svg>
                          <span style={{ fontSize: '13px' }}>{parsed.file.fileName}</span>
                        </a>
                      )}
                    </div>
                    <div style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '4px' }}>
                      {msg.timestamp ? new Date(msg.timestamp).toLocaleString('en-NZ', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                    </div>
                  </div>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          {selectedConv && (
            <div style={{ padding: '16px 20px', borderTop: '1px solid #E5E7EB', display: 'flex', gap: '10px', alignItems: 'center', flexShrink: 0 }}>
              <input type="file" ref={fileInputRef} onChange={handleFileUpload} style={{ display: 'none' }} />
              <button onClick={() => fileInputRef.current?.click()} disabled={uploadingFile}
                style={{
                  width: '36px', height: '36px', backgroundColor: '#F3F4F6', border: '1px solid #E5E7EB',
                  borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: uploadingFile ? 'not-allowed' : 'pointer', color: '#6B7280'
                }}>
                {uploadingFile ? (
                  <div style={{ width: '14px', height: '14px', border: '2px solid #9CA3AF', borderTop: '2px solid transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>
                )}
              </button>
              <input
                type="text" value={message} onChange={(e) => setMessage(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && !uploadingFile && sendMsg()}
                placeholder="Type a message..." disabled={uploadingFile}
                style={{ flex: 1, padding: '10px 16px', border: '1px solid #E5E7EB', borderRadius: '24px', fontSize: '14px', backgroundColor: '#F9FAFB', outline: 'none' }}
              />
              <button onClick={sendMsg} disabled={uploadingFile}
                style={{
                  width: '36px', height: '36px', backgroundColor: '#6B9080', border: 'none', borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'white'
                }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22,2 15,22 11,13 2,9 22,2"/></svg>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* New Conversation Modal */}
      {showNewConv && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '24px', width: '400px' }}>
            <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#1F2937', marginBottom: '16px', fontFamily: 'Georgia, serif' }}>New Message</h3>
            <p style={{ fontSize: '14px', color: '#6B7280', marginBottom: '16px' }}>Select who you'd like to message:</p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '20px', maxHeight: '300px', overflowY: 'auto' }}>
              {vendors.map(v => {
                const isSelected = selectedParticipants.includes(v.userId);
                return (
                  <div key={v.id}
                    onClick={() => {
                      if (!v.userId) return;
                      setSelectedParticipants(prev =>
                        isSelected ? prev.filter(p => p !== v.userId) : [...prev, v.userId]
                      );
                    }}
                    style={{
                      padding: '12px 16px', borderRadius: '8px', cursor: v.userId ? 'pointer' : 'not-allowed',
                      border: isSelected ? '2px solid #6B9080' : '1px solid #E5E7EB',
                      backgroundColor: isSelected ? '#E8F4E8' : 'white',
                      display: 'flex', alignItems: 'center', gap: '12px', opacity: v.userId ? 1 : 0.5
                    }}
                  >
                    <div style={{
                      width: '36px', height: '36px', borderRadius: '50%',
                      backgroundColor: isSelected ? '#6B9080' : '#E8F4E8',
                      color: isSelected ? 'white' : '#6B9080',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '13px', fontWeight: '600'
                    }}>
                      {v.name.substring(0, 2).toUpperCase()}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '15px', fontWeight: '500', color: '#1F2937' }}>{v.name}</div>
                    </div>
                    {isSelected && (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6B9080" strokeWidth="2.5"><polyline points="20,6 9,17 4,12"/></svg>
                    )}
                  </div>
                );
              })}
              {vendors.length === 0 && (
                <div style={{ padding: '20px', textAlign: 'center', color: '#9CA3AF', fontSize: '14px' }}>
                  No vendors linked to your event yet
                </div>
              )}
            </div>

            {selectedParticipants.length > 1 && (
              <div style={{ fontSize: '13px', color: '#6B9080', marginBottom: '12px', padding: '8px 12px', backgroundColor: '#E8F4E8', borderRadius: '6px' }}>
                This will create a group conversation with {selectedParticipants.length} participants
              </div>
            )}

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button onClick={() => { setShowNewConv(false); setSelectedParticipants([]); }}
                style={{ padding: '10px 20px', backgroundColor: 'white', color: '#6B7280', border: '1px solid #E5E7EB', borderRadius: '8px', fontSize: '14px', cursor: 'pointer' }}>
                Cancel
              </button>
              <button onClick={createConversation} disabled={selectedParticipants.length === 0}
                style={{
                  padding: '10px 20px', backgroundColor: selectedParticipants.length === 0 ? '#9CA3AF' : '#6B9080',
                  color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '500',
                  cursor: selectedParticipants.length === 0 ? 'not-allowed' : 'pointer'
                }}>
                Start Conversation
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default MessagesPage;
